import { DiseaseChartDay } from 'src/app/shared/components/disease-insight-chart/models';
import { BlightCropFieldAdvice, DailyAdvice, SprayAdvice } from '../../blight-v2/models/models';
import { getAdvice } from '../../blight-v2/shared/helpers/spray.helpers';
import { getAdviceColorKey, getAdviceTranslationKey, getAdviceTooltipKey, isAdviceHidden } from '../shared/helpers/advice-style.helper';
import {
  DownyMildewCalculationAPIStatus,
  DownyMildewDataAPIResponse,
  DownyMildewDayAPIResponse,
  DownyMildewResultAPIResponse,
  IrrigationAPIResponse,
} from './api-models';
import {
  DownyMildewCalculationStatus,
  DownyMildewCropFieldRegistration,
  DownyMildewData,
  DownyMildewDay,
  DownyMildewResult,
  Irrigation,
} from './models';

export const mapDownyMildewDataAPIResponses = (responses: DownyMildewDataAPIResponse[]): DownyMildewData[] =>
  responses.map(mapDownyMildewDataAPIResponse);

export const mapDownyMildewDataAPIResponse = (response: DownyMildewDataAPIResponse): DownyMildewData => ({
  ...response,
  plantDate: response.plantDate ? new Date(response.plantDate) : undefined,
  harvestDate: response.harvestDate ? new Date(response.harvestDate) : undefined,
  emergenceDate: response.emergenceDate ? new Date(response.emergenceDate) : undefined,
  downyMildewResult: mapDownyMildewResultAPIResponse(response.downyMildewResult),
  irrigations: response.irrigations?.map(mapIrrigationAPIResponse) ?? [],
});

export const mapDownyMildewResultAPIResponse = (response: DownyMildewResultAPIResponse): DownyMildewResult => ({
  ...response,
  calculatedOn: new Date(response.calculatedOn),
  calculationStatus: mapDownyMildewResultStatus(response.calculationStatus),
  days: response.days ? response.days.map(mapDownyMildewDayAPIResponse) : [],
  today: response.today ? mapDownyMildewDayAPIResponse(response.today) : undefined,
});

const mapDownyMildewResultStatus = (status: DownyMildewCalculationAPIStatus): DownyMildewCalculationStatus => {
  switch (status) {
    case 'None':
      return 'none';
    case 'Initiated':
      return 'initiated';
    case 'Completed':
      return 'completed';
    case 'Failed':
      return 'failed';
    default:
      throw new Error(`Unknown Downy Mildew Calculation Status: ${status}`);
  }
}

export const mapDownyMildewDayAPIResponse = (response: DownyMildewDayAPIResponse): DownyMildewDay => ({
  ...response,
  date: new Date(response.date),
  sprayTime: response.sprayTime ? new Date(response.sprayTime) : undefined,
});

const mapIrrigationAPIResponse = (response: IrrigationAPIResponse): Irrigation => ({
  ...response,
  startDate: new Date(response.startDate),
  endDate: new Date(response.endDate),
});

export const mapDownyMildewCropFieldAdvice = (data: DownyMildewData): BlightCropFieldAdvice => {
  const days = data.downyMildewResult?.days ?? [];
  const todaysAdvice = data.downyMildewResult?.today ?? getBestDayForSorting(days);

  return {
    cropFieldId: data.cropFieldId,
    cropFieldName: data.cropFieldName,
    cropVarietyName: data.cropVarietyName,
    amountOfHectares: data.areaInHectares,
    cropYear: data.year,
    emergenceDate: data.emergenceDate,
    plantDate: data.plantDate,
    harvestDate: data.harvestDate,
    advices: days.map(mapAdvices),
    todaysAdvice: todaysAdvice ? mapAdvices(todaysAdvice) : undefined,
    lastSprayDate: getLastSprayDate(days),
    isCropFieldVisible: true,
  };
};

const getTodayFromDays = (days: DownyMildewDay[]): DownyMildewDay | undefined => {
  const now = new Date();

  return days.find(
    (day) =>
      day.date.getFullYear() === now.getFullYear() &&
      day.date.getMonth() === now.getMonth() &&
      day.date.getDate() === now.getDate(),
  );
};

const getBestDayForSorting = (days: DownyMildewDay[]): DownyMildewDay | undefined => {
  if (!days.length) {
    return undefined;
  }

  const today = getTodayFromDays(days);
  if (today) {
    return today;
  }

  const sprayPriority = {
    [SprayAdvice.stop]: 0,
    [SprayAdvice.curative]: 1,
    [SprayAdvice.preventive]: 2,
    [SprayAdvice.cropsProtected]: 3,
    [SprayAdvice.noAdvice]: 4,
  };

  return [...days].sort((a, b) => {
    const priorityA = sprayPriority[getSortSprayAdvice(a)];
    const priorityB = sprayPriority[getSortSprayAdvice(b)];

    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }

    return a.date.getTime() - b.date.getTime();
  })[0];
};

const getLastSprayDate = (days: DownyMildewDay[]): Date | undefined => {
  const sprayTimes = days
    .map((day) => day.sprayTime)
    .filter((sprayTime): sprayTime is Date => !!sprayTime);

  const protectedDates = days
    .filter((day) => (day.sprayProtectionPercentage ?? 0) > 0)
    .map((day) => day.date);

  const dateCandidates = [...sprayTimes, ...protectedDates];

  if (!dateCandidates.length) {
    return undefined;
  }

  return new Date(Math.max(...dateCandidates.map((d) => d.getTime())));
};

const getSortSprayAdvice = (day: DownyMildewDay): SprayAdvice => {
  if (isAdviceHidden(day)) {
    return SprayAdvice.noAdvice;
  }

  if (day.sprayType != null) {
    return getAdvice(day.sprayType);
  }

  if (day.sprayRecommended) {
    return SprayAdvice.stop;
  }

  if ((day.sprayProtectionPercentage ?? 0) > 0) {
    return SprayAdvice.cropsProtected;
  }

  return SprayAdvice.noAdvice;
};

const mapAdvices = (day: DownyMildewDay): DailyAdvice => ({
  date: new Date(day.date),
  sprayAdvice: getSortSprayAdvice(day),
  remainingFungicidePercentage: day.fungicideLeftPct ?? 0,
  isSprayedToday: !!day.sprayTime,
  ...(day.sprayType == null
    ? {
      translationKey: getAdviceTranslationKey(day),
      stylingOverride: getAdviceColorKey(day),
    }
    : {}),
});

export const mapDownyMildewDataToCropFieldRegistration = (data: DownyMildewData): DownyMildewCropFieldRegistration => ({
  cropFieldId: data.cropFieldId,
  year: data.year,
  plantDate: data.plantDate ?? new Date(),
  emergenceDate: data.emergenceDate ?? new Date(),
  harvestDate: data.harvestDate ?? new Date(),
  isPlantDateConfirmed: data.isPlantDateConfirmed,
  externalCropFieldId: '',
  feature: '',
  created: new Date(),
  updated: new Date(),
});

export function mapDownyMildewDaysToChartDays(days: DownyMildewDay[], irrigations: Irrigation[] = []): DiseaseChartDay[] {
  return days.map((day) => ({
    date: day.date,
    sprayTime: day.sprayTime,
    sprayType: toNumber(day.sprayType),
    fungicideLeftPct: toNumber(day.fungicideLeftPct),
    weather: (day.hourlyWeather ?? []).map((weather) => ({
      hour: toNumber(weather.hour) ?? 0,
      temperature: toNumber(weather.temperatureCelsius),
      humidity: toNumber(weather.relativeHumidityPercent),
      precipitation: toNumber(weather.precipitationMm),
      irrigation: getHourlyIrrigation(day.date, toNumber(weather.hour) ?? 0, irrigations),
    })),
    sporulationRiskPercentage: toNumber(day.sporulationRiskPercentage),
    sporulationRiskLevel: day.sporulationRiskLevel,
    infectionRiskPercentage: toNumber(day.infectionRiskPercentage),
    infectionRiskLevel: day.infectionRiskLevel,
    combinedRiskPercentage: toNumber(day.combinedRiskPercentage),
    combinedRiskLevel: day.combinedRiskLevel,
    leafWetnessHours: toNumber(day.leafWetnessHours),
    averageTemperatureCelsius: toNumber(day.averageTemperatureCelsius),
    averageRelativeHumidityPercent: toNumber(day.averageRelativeHumidityPercent),
    precipitationMm: toNumber(day.precipitationMm),
    sprayRecommended: day.sprayRecommended,
    sprayProtectionPercentage: toNumber(day.sprayProtectionPercentage),
    adviceTranslationKey: getAdviceTranslationKey(day),
    adviceTooltipKey: getAdviceTooltipKey(day),
    adviceStylingOverride: getAdviceColorKey(day),
  }));
}

function getHourlyIrrigation(dayDate: Date, hour: number, irrigations: Irrigation[]): number | undefined {
  if (!irrigations.length) return undefined;

  const hourStart = Date.UTC(dayDate.getUTCFullYear(), dayDate.getUTCMonth(), dayDate.getUTCDate(), hour);
  const hourEnd = Date.UTC(dayDate.getUTCFullYear(), dayDate.getUTCMonth(), dayDate.getUTCDate(), hour + 1);

  const irrigationMm = irrigations.reduce((sum, irrigation) => {
    const irrigationStart = new Date(irrigation.startDate).getTime();
    const irrigationEnd = new Date(irrigation.endDate).getTime();
    const durationMs = irrigationEnd - irrigationStart;

    if (durationMs <= 0 || irrigation.dosageMm == null) {
      return sum;
    }

    const overlapStart = Math.max(hourStart, irrigationStart);
    const overlapEnd = Math.min(hourEnd, irrigationEnd);

    if (overlapEnd <= overlapStart) {
      return sum;
    }

    const overlapRatio = (overlapEnd - overlapStart) / durationMs;
    return sum + irrigation.dosageMm * overlapRatio;
  }, 0);

  return irrigationMm > 0 ? Number.parseFloat(irrigationMm.toFixed(3)) : undefined;
}

const toNumber = (value: number | string | undefined | null): number | undefined =>
  value == null ? undefined : Number(value);
